import * as React from 'react';

import { styled } from '@mui/material';
import { NumberUtils } from '../utils';
import { CerebroInput } from './CerebroInput';
import { fontFamily, weights } from '../context/CerebroThemeProvider';
import { useDebounce } from '../hooks';

type CerebroInputProps = React.ComponentPropsWithoutRef<typeof CerebroInput>;

type CerebroMaskedInputProps = Omit<CerebroInputProps, 'value'> & {
	value: string | number | undefined;
	formatAs?: NumberUtils.FormatStyles;

	allowDecimals?: boolean;
	minDecimalPlaces?: number;
	maxDecimalPlaces?: number;
};

const Input: React.FC<CerebroMaskedInputProps> = ({
	formatAs = 'decimal',

	minDecimalPlaces,
	maxDecimalPlaces,
	allowDecimals,

	onChange,
	onBlur: handleBlur,
	onFocus: handleFocus,

	...props
}) => {
	const format = React.useCallback(
		(value: number | string | undefined, allowHyphen: boolean) => {
			if (!value) return '';
			if (value === '-' && allowHyphen) return value;
			const updatedAllowDecimals = allowDecimals ?? false;

			let options: NumberUtils.FormatOptions = {
				minimumFractionDigits: updatedAllowDecimals ? minDecimalPlaces : 0,
				maximumFractionDigits: updatedAllowDecimals ? maxDecimalPlaces : 0,
			};

			return typeof value === 'number'
				? NumberUtils.FormatNumber(value, formatAs, options) ?? ''
				: NumberUtils.FormatStringAsNumber(value, formatAs, options) ?? '';
		},
		[formatAs, allowDecimals, minDecimalPlaces, maxDecimalPlaces]
	);

	const debouncedValue = useDebounce(props.value, 1000);

	React.useEffect(() => {
		onChange?.({
			target: {
				id: props.id,
				name: props.name,
				value: format(debouncedValue, true),
			},
		} as React.ChangeEvent<HTMLInputElement>);
	}, [debouncedValue, props.id, props.name, onChange, format]);

	const onFocus: React.FocusEventHandler<HTMLInputElement> = event => {
		handleFocus?.(event);
		event.target.select();
	};

	const onBlur: React.FocusEventHandler<HTMLInputElement> = event => {
		event.target.value = format(event.target.value, false);

		onChange?.(event);
		handleBlur?.(event);
	};

	return <CerebroInput onFocus={onFocus} onChange={onChange} onBlur={onBlur} {...props} />;
};

export const CerebroMaskedInput = styled(Input, { shouldForwardProp: prop => prop !== 'error' })(
	({ error, disabled, readOnly, theme }) => ({
		borderWidth: 2,
		borderStyle: 'solid',
		borderRadius: theme.shape.borderRadius,
		borderColor: error
			? theme.palette.error.main
			: disabled || readOnly
			? theme.palette.gray.light
			: theme.palette.gray.medium,

		color: disabled ? theme.palette.gray.light : theme.palette.gray.dark,
		cursor: disabled ? 'not-allowed' : 'initial',

		fontFamily,
		fontSize: 12,
		fontWeight: disabled ? weights.medium : weights.semibold,

		lineHeight: '14px',
		marginBottom: theme.spacing(1),
		marginTop: theme.spacing(1.5),
		padding: '14px 12px',

		outline: 'none',
		width: '100%',

		'&:focus:not(:read-only)': {
			borderColor: error ? theme.palette.error.light : theme.palette.primary.main,
		},

		'&::placeholder': {
			color: theme.palette.gray.medium,
		},
	})
);
